import * as THREE from 'three';
import type { ShadowCaster } from '../render/Shadows';

/**
 * Generic instanced-LOD renderer.
 * Each variant has up to N LOD geometries; instances are bucketed per (variant, lod) every few frames
 * based on distance, with coarse cell-based culling. Main and shadow passes share the same buckets.
 *
 * Per-instance attributes: aInst0 = (x, y, z, rotY), aInst1 = (sx, sy, sz, rand).
 */
export interface SubMesh {
  geometry: THREE.BufferGeometry;
  /** index into the system's material list */
  material: number;
}

export interface InstanceVariant {
  name: string;
  /** one geometry per LOD (single material) or a list of sub-meshes per LOD (multi-material) */
  lods: (THREE.BufferGeometry | SubMesh[])[];
  /** bounding radius of the unscaled LOD0 geometry */
  radius: number;
}

export interface LodPolicy {
  /** distance thresholds (meters) for switching LOD k -> k+1, scaled by instance size factor */
  switchDist: number[];
  /** maximum draw distance */
  maxDist: number;
  /** distances are multiplied by (instanceRadius / sizeRef) */
  sizeRef: number;
  /** only instances closer than this cast shadows (default: all drawn instances) */
  shadowDist?: number;
  /** per-cascade shadow distances (overrides shadowDist); cascades beyond the list cast nothing */
  shadowDists?: number[];
}

interface Bucket {
  geometries: THREE.InstancedBufferGeometry[];
  a0: THREE.InstancedBufferAttribute;
  a1: THREE.InstancedBufferAttribute;
  meshes: THREE.Mesh[];
  depthMeshes: THREE.Mesh[];
  count: number;
  capacity: number;
  bins: number[][];
  binEnd: number[]; // cumulative instance count after each bin
}

export class InstanceSystem implements ShadowCaster {
  readonly group = new THREE.Group();
  readonly shadowScene = new THREE.Scene();
  private readonly buckets: Bucket[][] = []; // [variant][lod]
  // SoA instance store
  private n = 0;
  private data0: Float32Array = new Float32Array(0);
  private data1: Float32Array = new Float32Array(0);
  private variantOf: Uint16Array = new Uint16Array(0);
  private radiusOf = new Float32Array(0);
  // cells
  private cellSize = 192;
  private cells = new Map<number, number[]>();
  private cellBounds = new Map<number, THREE.Sphere>();
  private lastPos = new THREE.Vector3(1e9, 0, 0);
  private lastDir = new THREE.Vector3();
  private frame = 0;
  castShadows = true;
  shadowMaxCascade = 3;
  drawnInstances = 0;
  private readonly frustum = new THREE.Frustum();
  private readonly m4 = new THREE.Matrix4();

  private readonly materials: THREE.Material[];
  private readonly shadowBins: number[];
  private readonly depthMaterials: (THREE.Material | null)[];

  constructor(readonly variants: InstanceVariant[], material: THREE.Material | THREE.Material[], depthMaterial: THREE.Material | null | (THREE.Material | null)[], readonly policy: LodPolicy) {
    this.materials = Array.isArray(material) ? material : [material];
    this.shadowBins = policy.shadowDists ?? [policy.shadowDist ?? Infinity];
    this.depthMaterials = Array.isArray(depthMaterial) ? depthMaterial : [depthMaterial];
    for (let v = 0; v < variants.length; v++) {
      const row: Bucket[] = [];
      for (let l = 0; l < variants[v].lods.length; l++) row.push(this.makeBucket(variants[v].lods[l], 16));
      this.buckets.push(row);
    }
    this.group.name = 'instances';
  }

  private makeBucket(src: THREE.BufferGeometry | SubMesh[], capacity: number): Bucket {
    const subs: SubMesh[] = Array.isArray(src) ? src : [{ geometry: src, material: 0 }];
    const a0 = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    const a1 = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 4), 4);
    a0.setUsage(THREE.DynamicDrawUsage);
    a1.setUsage(THREE.DynamicDrawUsage);
    const nb = this.shadowBins.length + 1;
    const b: Bucket = { geometries: [], a0, a1, meshes: [], depthMeshes: [], count: 0, capacity, bins: Array.from({ length: nb }, () => []), binEnd: new Array(nb).fill(0) };
    for (const sm of subs) {
      const geometry = new THREE.InstancedBufferGeometry();
      geometry.index = sm.geometry.index;
      for (const [k, attr] of Object.entries(sm.geometry.attributes)) geometry.setAttribute(k, attr);
      geometry.setAttribute('aInst0', a0);
      geometry.setAttribute('aInst1', a1);
      geometry.instanceCount = 0;
      const mesh = new THREE.Mesh(geometry, this.materials[sm.material] ?? this.materials[0]);
      mesh.frustumCulled = false;
      this.group.add(mesh);
      b.geometries.push(geometry);
      b.meshes.push(mesh);
      const dm = this.depthMaterials[sm.material] ?? (this.depthMaterials.length === 1 ? this.depthMaterials[0] : null);
      if (dm) {
        const depthMesh = new THREE.Mesh(geometry, dm);
        depthMesh.frustumCulled = false;
        this.shadowScene.add(depthMesh);
        b.depthMeshes.push(depthMesh);
      }
    }
    return b;
  }

  private grow(b: Bucket, need: number) {
    let cap = b.capacity;
    while (cap < need) cap *= 2;
    const a0 = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    const a1 = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    a0.setUsage(THREE.DynamicDrawUsage);
    a1.setUsage(THREE.DynamicDrawUsage);
    for (const g of b.geometries) { g.setAttribute('aInst0', a0); g.setAttribute('aInst1', a1); }
    b.a0 = a0; b.a1 = a1; b.capacity = cap;
  }

  /** Replace all instances. Arrays are flat: inst0 = [x,y,z,rot]*, inst1 = [sx,sy,sz,rand]*. */
  setInstances(inst0: Float32Array, inst1: Float32Array, variant: Uint16Array) {
    this.n = variant.length;
    this.data0 = inst0;
    this.data1 = inst1;
    this.variantOf = variant;
    this.radiusOf = new Float32Array(this.n);
    this.cells.clear();
    this.cellBounds.clear();
    for (let i = 0; i < this.n; i++) {
      const v = this.variants[variant[i]];
      const s = Math.max(inst1[i * 4], inst1[i * 4 + 1], inst1[i * 4 + 2]);
      this.radiusOf[i] = v.radius * s;
      const x = inst0[i * 4], z = inst0[i * 4 + 2];
      const key = this.cellKey(x, z);
      let a = this.cells.get(key);
      if (!a) this.cells.set(key, (a = []));
      a.push(i);
    }
    for (const [key, idx] of this.cells) {
      const box = new THREE.Box3();
      for (const i of idx) {
        const r = this.radiusOf[i];
        box.expandByPoint(new THREE.Vector3(inst0[i * 4] - r, inst0[i * 4 + 1] - r, inst0[i * 4 + 2] - r));
        box.expandByPoint(new THREE.Vector3(inst0[i * 4] + r, inst0[i * 4 + 1] + r * 2, inst0[i * 4 + 2] + r));
      }
      this.cellBounds.set(key, box.getBoundingSphere(new THREE.Sphere()));
    }
    this.lastPos.set(1e9, 0, 0);
  }

  private cellKey(x: number, z: number) {
    return (Math.floor(x / this.cellSize) + 4096) * 8192 + (Math.floor(z / this.cellSize) + 4096);
  }

  get count() {
    return this.n;
  }

  /** Re-bucket instances by LOD. Cheap enough to run every few frames. */
  update(camera: THREE.PerspectiveCamera, lodScale = 1, force = false) {
    this.frame++;
    const pos = camera.position;
    const dir = camera.getWorldDirection(new THREE.Vector3());
    const moved = pos.distanceToSquared(this.lastPos) > 1.0 || dir.dot(this.lastDir) < 0.995;
    if (!force && !moved && this.frame % 30 !== 0) return;
    this.lastPos.copy(pos);
    this.lastDir.copy(dir);
    this.m4.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.frustum.setFromProjectionMatrix(this.m4);
    for (const row of this.buckets) for (const b of row) { b.count = 0; for (const bin of b.bins) bin.length = 0; }
    const sb = this.shadowBins;
    const shMax = sb.length ? sb[sb.length - 1] : 0;
    const p = this.policy;
    const maxD = p.maxDist * lodScale;
    const shD = shMax;
    const shadowKeep = 260; // keep off-screen cells nearby for shadow casting
    let drawn = 0;
    const d0 = this.data0, d1 = this.data1;
    for (const [key, idx] of this.cells) {
      const sph = this.cellBounds.get(key)!;
      const cd = sph.center.distanceTo(pos) - sph.radius;
      if (cd > maxD) continue;
      if (!this.frustum.intersectsSphere(sph) && cd > Math.min(shadowKeep, shD)) continue;
      for (const i of idx) {
        const dx = d0[i * 4] - pos.x, dy = d0[i * 4 + 1] - pos.y, dz = d0[i * 4 + 2] - pos.z;
        const r = this.radiusOf[i];
        const dist = Math.sqrt(dx * dx + dy * dy + dz * dz) - r;
        const f = Math.max(0.35, r / p.sizeRef) * lodScale;
        if (dist > maxD * Math.min(1, f)) continue;
        const row = this.buckets[this.variantOf[i]];
        let l = 0;
        while (l < row.length - 1 && dist > p.switchDist[l] * f) l++;
        const b = row[l];
        let bi = 0;
        while (bi < sb.length && dist >= sb[bi]) bi++;
        b.bins[bi].push(i);
        drawn++;
      }
    }
    for (const row of this.buckets) for (const b of row) {
      let total = 0;
      for (const bin of b.bins) total += bin.length;
      if (total > b.capacity) this.grow(b, total);
      const A0 = b.a0.array as Float32Array, A1 = b.a1.array as Float32Array;
      let o = 0, n = 0;
      b.bins.forEach((bin, k) => {
        for (const i of bin) {
          A0[o] = d0[i * 4]; A0[o + 1] = d0[i * 4 + 1]; A0[o + 2] = d0[i * 4 + 2]; A0[o + 3] = d0[i * 4 + 3];
          A1[o] = d1[i * 4]; A1[o + 1] = d1[i * 4 + 1]; A1[o + 2] = d1[i * 4 + 2]; A1[o + 3] = d1[i * 4 + 3];
          o += 4;
        }
        n += bin.length;
        b.binEnd[k] = n;
      });
      b.count = total;
    }
    for (const row of this.buckets) for (const b of row) {
      for (const g of b.geometries) g.instanceCount = b.count;
      for (const m of b.meshes) m.visible = b.count > 0;
      for (const m of b.depthMeshes) m.visible = b.binEnd[0] > 0 || (this.shadowBins.length > 1 && b.binEnd[this.shadowBins.length - 1] > 0);
      if (b.count > 0) {
        b.a0.clearUpdateRanges(); b.a1.clearUpdateRanges();
        b.a0.addUpdateRange(0, b.count * 4); b.a1.addUpdateRange(0, b.count * 4);
        b.a0.needsUpdate = true; b.a1.needsUpdate = true;
      }
    }
    this.drawnInstances = drawn;
  }

  renderShadow(renderer: THREE.WebGLRenderer, cam: THREE.OrthographicCamera, cascade: number, _nearDist: number) {
    void _nearDist;
    if (!this.castShadows || cascade > this.shadowMaxCascade) return;
    // only the shadow-distance prefix of each bucket casts shadows into this cascade
    const k = Math.min(cascade, this.shadowBins.length - 1);
    if (this.policy.shadowDists && cascade >= this.shadowBins.length) return;
    let any = false;
    for (const row of this.buckets) for (const b of row) {
      const c = b.binEnd[k];
      for (const g of b.geometries) g.instanceCount = c;
      for (const m of b.depthMeshes) m.visible = c > 0;
      any = any || c > 0;
    }
    if (any) renderer.render(this.shadowScene, cam);
    for (const row of this.buckets) for (const b of row) for (const g of b.geometries) g.instanceCount = b.count;
  }

  /** For debugging / diagnostics */
  stats() {
    let draws = 0;
    const perLod: number[] = [];
    const trisPerLod: number[] = [];
    for (const row of this.buckets) row.forEach((b, l) => {
      if (b.count > 0) draws += b.meshes.length;
      perLod[l] = (perLod[l] ?? 0) + b.count;
      let t = 0;
      for (const g of b.geometries) t += (g.index ? g.index.count : g.getAttribute('position').count) / 3;
      trisPerLod[l] = (trisPerLod[l] ?? 0) + t * b.count;
    });
    return { instances: this.n, drawn: this.drawnInstances, drawCalls: draws, perLod, trisPerLod };
  }
}
